import {
  v2AdminPortalRequestsControllerApprove,
  v2AdminPortalRequestsControllerCreateRequest,
  v2AdminPortalRequestsControllerDeleteDocument,
  v2AdminPortalRequestsControllerDeleteFieldReview,
  v2AdminPortalRequestsControllerDetail,
  v2AdminPortalRequestsControllerExtend,
  v2AdminPortalRequestsControllerList,
  v2AdminPortalRequestsControllerListDocuments,
  v2AdminPortalRequestsControllerMarkVerified,
  v2AdminPortalRequestsControllerReject,
  v2AdminPortalRequestsControllerRequestChanges,
  v2AdminPortalRequestsControllerResend,
  v2AdminPortalRequestsControllerRevalidate,
  v2AdminPortalRequestsControllerRevalidationMetrics,
  v2AdminPortalRequestsControllerRevealLink,
  v2AdminPortalRequestsControllerRevoke,
  v2AdminPortalRequestsControllerRotateLink,
  v2AdminPortalRequestsControllerSaveFieldReviews,
  v2AdminPortalRequestsControllerTakeOver,
  v2AdminPortalRequestsControllerUploadDocument
} from '@idc-forms/api-client'
import {
  V2AdminPortalDocumentsSchema,
  V2AdminPortalDossierStoredSchema,
  V2AdminPortalRequestSchema,
  V2AdminPortalRequestsListSchema,
  V2PortalLinkSchema,
  V2RevalidationMetricsSchema
} from '@idc-forms/schemas/api-v2/admin-portal-requests'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * El portal del cliente contra el contrato v2 (carril D).
 *
 * ── ☠️ EL EXPEDIENTE SE DIRECCIONA POR `pfr_`, NO POR EL UUID ───────────────
 *
 * v1 usa el `id` uuid del expediente y v2 no lo acepta. El cambio no es cosmético: hasta el
 * 2026-08-12 el `pfr_` se lo llevaba **la credencial**, y separarlos fue la decisión Nº 44. Una
 * pantalla que le pase un uuid a estas funciones recibe un `400`, y eso es correcto — es la
 * frontera diciendo que el id viejo no cruza.
 *
 * ── LO QUE ESTE SERVICIO NO HACE ────────────────────────────────────────────
 *
 * **Ya no arma el enlace del portal.** v1 devolvía el `accessToken` en cada respuesta y la pantalla
 * construía la URL en el navegador; v2 no lo devuelve **nunca**, porque abre el KYC de un cliente
 * sin autenticar y todo lo que entra a una respuesta termina en un cache. El correo lo manda el
 * servidor: crear ya envía, y `resend` reenvía.
 *
 * ── POR QUÉ CADA RESPUESTA PASA POR `assertShape` ───────────────────────────
 *
 * Igual que el resto de la ola: el SDK está generado del OpenAPI, así que sus tipos describen lo
 * que el contrato **dice**. `assertShape` verifica lo que el servidor **manda**. Cuando los dos
 * discrepan —un despliegue a mitad de camino, un campo que se fue— el error aparece acá, con el
 * nombre del campo, en vez de tres componentes más abajo como un `undefined`.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
export type PortalRequestsListParams = {
  /** El `cli_` del cliente. Opcional: v1 lo EXIGÍA, y por eso no podía responder «qué espera revisión». */
  client?: string
  status?: string
  validationType?: string
  search?: string
  limit?: number
  cursor?: string
}

export const portalAdminV2Service = {
  list: async (accessToken: string, params: PortalRequestsListParams = {}) => {
    const result = await v2AdminPortalRequestsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: {
        // Los filtros viajan con la forma del ADR 0020. Se arman acá y no en cada pantalla para
        // que el día que el allowlist gane un campo, haya un solo lugar donde enterarse.
        ...(params.client ? { 'filters[client][$eq]': params.client } : {}),
        ...(params.status ? { 'filters[status][$eq]': params.status } : {}),
        ...(params.validationType ? { 'filters[validationType][$eq]': params.validationType } : {}),
        ...(params.search ? { search: params.search } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {})
      } as never
    })
    return assertShape(V2AdminPortalRequestsListSchema, result.data)
  },

  /**
   * El DOSSIER: la ficha más el contenido, las marcas, el resumen por sección y los documentos.
   *
   * Es lo que la pantalla de revisión necesita entero — y lo que el detalle v2 **no daba** hasta la
   * tajada H, medido en 52 usos de la web.
   */
  detail: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPortalDossierStoredSchema, result.data, 'portal.dossier')
  },

  /**
   * Abre el expediente **y manda el correo**, en una llamada.
   *
   * v1 necesitaba dos —crear y después enviar— y además le pedía a la pantalla el tipo y el nombre
   * del cliente, que ahora los deriva el servidor del registro. Una pantalla que los mandaba podía
   * abrir un expediente `individual` sobre una persona jurídica.
   */
  create: async (
    accessToken: string,
    body: { client: string; flowDefinition?: string; clientEmail?: string; expirationDays?: number }
  ) => {
    const result = await v2AdminPortalRequestsControllerCreateRequest({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body: body as never
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /*
   * ── LAS SIETE TRANSICIONES ───────────────────────────────────────────────
   *
   * Las siete devuelven el expediente ya movido, así que la pantalla no necesita volver a pedirlo.
   * Y las siete pueden responder `409 PORTAL_TRANSITION_NOT_ALLOWED` con el estado actual en
   * `details`: la guarda del servidor corta ANTES del efecto, así que un rechazo no deja nada a
   * medias y reintentar es seguro.
   */

  takeOver: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerTakeOver({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  approve: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerApprove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  requestChanges: async (accessToken: string, id: string, comment?: string) => {
    const result = await v2AdminPortalRequestsControllerRequestChanges({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: (comment ? { comment } : {}) as never
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /** El motivo es OBLIGATORIO: se le muestra al cliente. */
  reject: async (accessToken: string, id: string, reason: string) => {
    const result = await v2AdminPortalRequestsControllerReject({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { reason } as never
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /** El motivo es opcional: la revocación NO se le notifica al cliente, es auditoría interna. */
  revoke: async (accessToken: string, id: string, reason?: string) => {
    const result = await v2AdminPortalRequestsControllerRevoke({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: (reason ? { reason } : {}) as never
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /** Omitir `expirationDays` reusa el plazo con el que nació el expediente. */
  extend: async (accessToken: string, id: string, expirationDays?: number) => {
    const result = await v2AdminPortalRequestsControllerExtend({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: (expirationDays ? { expirationDays } : {}) as never
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  resend: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerResend({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /*
   * ── EL ENLACE, PARA COMPARTIRLO POR FUERA DEL CORREO ─────────────────────
   *
   * ☠️ **Lo que devuelven estas dos NO se guarda.** Es la credencial que abre el KYC del cliente
   * sin autenticar, y viaja por la única puerta del contrato que la entrega.
   *
   * Concretamente, para quien las use:
   *
   * - **`useMutation`, nunca `useQuery`.** El resultado de una query ES un cache, y queda ahí
   *   después de que la pantalla se fue.
   * - **No se renderiza como texto.** Un `<input value={url}>` la deja en capturas de pantalla, en
   *   dumps del DOM y en el session replay de Sentry. Va al portapapeles; el toast dice «Enlace
   *   copiado», no el enlace.
   *
   * Las dos exigen `RevealPortalLink`, que no viaja con leer ni con extender.
   */

  /** Devuelve el enlace vigente. No cambia nada — y aun así queda en la bitácora. */
  revealLink: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerRevealLink({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2PortalLinkSchema, result.data)
  },

  /**
   * Acuña uno nuevo y **mata el anterior**.
   *
   * ⚠️ Deja muerto el enlace que ya se mandó por correo: la pantalla tiene que decirlo antes de
   * disparar. Es para contener una filtración, no para «actualizar» el enlace.
   */
  rotateLink: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerRotateLink({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2PortalLinkSchema, result.data)
  },

  /*
   * ── REVISIÓN POR CAMPO ───────────────────────────────────────────────────
   *
   * Las dos exigen que el expediente esté EN REVISIÓN, y v2 es más estricto que v1 a propósito:
   * anotar mientras el cliente llena le escribe correcciones sobre datos que va a cambiar. La forma
   * de anotar antes es `takeOver`, que le corta el llenado en vez de anotarlo por atrás.
   */

  saveFieldReviews: async (
    accessToken: string,
    id: string,
    reviews: Array<{
      field: string
      section: string
      scope?: string
      status: 'approved' | 'correction' | 'not_applicable'
      comment?: string | null
    }>
  ) => {
    const result = await v2AdminPortalRequestsControllerSaveFieldReviews({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { reviews } as never
    })
    return assertShape(V2AdminPortalDossierStoredSchema, result.data, 'portal.saveFieldReviews')
  },

  /**
   * El campo va en el CUERPO y no en el path — es una ruta con puntos e índices de arreglo
   * (`applicant.identificationCard.number`), y un segmento de URL que los lleva es un problema de
   * ruteo disfrazado de identificador.
   */
  deleteFieldReview: async (accessToken: string, id: string, field: string, section: string) => {
    const result = await v2AdminPortalRequestsControllerDeleteFieldReview({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { field, section } as never
    })
    return assertShape(V2AdminPortalDossierStoredSchema, result.data, 'portal.deleteFieldReview')
  },

  /*
   * ── DOCUMENTOS ───────────────────────────────────────────────────────────
   */

  listDocuments: async (accessToken: string, id: string) => {
    const result = await v2AdminPortalRequestsControllerListDocuments({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminPortalDocumentsSchema, result.data)
  },

  /** Se sube contra un SLOT (`requirementId`), no contra una carpeta. */
  uploadDocument: async (accessToken: string, id: string, requirementId: string, file: File) => {
    const result = await v2AdminPortalRequestsControllerUploadDocument({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body: { file, requirementId } as never
    })
    return result.data
  },

  deleteDocument: async (accessToken: string, id: string, documentId: string): Promise<void> => {
    await v2AdminPortalRequestsControllerDeleteDocument({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id, documentId }
    })
  },

  /*
   * ── REVALIDACIÓN ─────────────────────────────────────────────────────────
   */

  /** Se direcciona por el `cli_` del CLIENTE: el expediente todavía no existe — es lo que crea. */
  revalidate: async (accessToken: string, client: string) => {
    const result = await v2AdminPortalRequestsControllerRevalidate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { client }
    })
    return assertShape(V2AdminPortalRequestSchema, result.data)
  },

  /** Sella la verificación a mano y reinicia el reloj: para el cliente que revalidó en la oficina. */
  markVerified: async (accessToken: string, client: string) => {
    const result = await v2AdminPortalRequestsControllerMarkVerified({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { client }
    })
    return result.data
  },

  revalidationMetrics: async (accessToken: string) => {
    const result = await v2AdminPortalRequestsControllerRevalidationMetrics({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2RevalidationMetricsSchema, result.data)
  }
}
